import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { getDropdownSettingsApi } from '../rtk-apis/clinics/getDropdownSettings';
import {
  AORTA_ASCENDING_OPTIONS,
  AORTA_ASCENDING_SLOT,
  AORTA_SLOT,
  AORTA_SLOT_OPTIONS,
} from './aortaSlot';

/**
 * Shared source of truth for the clinic's per-clinic report-editor dropdown
 * options. Fetches once (on first mount, if not already loaded/loading) and
 * exposes a `key -> options[]` map built from the API response.
 *
 * No default lists — a clinic with no dropdowns configured just gets an
 * empty map, and callers should fall back to an empty options array.
 */
/**
 * Options for slots that no clinic has a list for yet.
 *
 * Seeded first, so any entry the API returns overwrites them — report wording belongs to
 * the clinic. Only here so a slot with no configured list still opens with a usable menu
 * instead of an empty one. Remove an entry once its rows are seeded.
 */
const CODE_DEFAULTS: Record<string, string[]> = {
  [AORTA_SLOT]: AORTA_SLOT_OPTIONS,
  [AORTA_ASCENDING_SLOT]: AORTA_ASCENDING_OPTIONS,
};

export const useClinicDropdowns = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { result, loading, status } = useSelector(
    (state: RootState) => state.getDropdownSettingsResponse
  );

  useEffect(() => {
    // status guard: a failed fetch leaves result null — without it this
    // effect would re-dispatch forever on an erroring backend.
    if (!result && !loading && !status) {
      dispatch(getDropdownSettingsApi());
    }
  }, [result, loading, status, dispatch]);

  const dropdownMap = useMemo<Record<string, string[]>>(() => {
    const dropdowns = result?.result?.dropdowns;
    if (!Array.isArray(dropdowns)) {
      return { ...CODE_DEFAULTS };
    }
    return dropdowns.reduce(
      (acc: Record<string, string[]>, entry: { key?: string; options?: string[] }) => {
        if (entry?.key) {
          acc[entry.key] = Array.isArray(entry.options) ? entry.options : [];
        }
        return acc;
      },
      { ...CODE_DEFAULTS }
    );
  }, [result]);

  const refresh = () => {
    dispatch(getDropdownSettingsApi());
  };

  return { dropdownMap, refresh };
};
